import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { currencyFormatter } from '../../../utils/currencyFormat';
import DocumentActions from '../../common/DocumentActions';
import CustomPagination from '../../../components/customPagination';
import StatusChip from '../../../components/CustomStatusChip';
import { usePdfActions } from '../../common/DocumentPdf';
import { get } from 'lodash';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import { useGetRefundsHandler } from '../../refunds/dataHandlers/getRefundsHandler';
import { getDateInMysTzFormatted } from '../../../utils/formatDate';

const SBRefundTable = ({ handlePaginationChange, searchQuery }) => {
  const navigate = useNavigate();
  const { downloadPdf, handlePrintPdf } = usePdfActions();

  const { refunds, totalDocuments, isLoading } = useGetRefundsHandler({ searchQuery });

  const columns = [
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      renderCell: ({ row }) => <StatusChip status={row.irbm_response.status} />,
    },
    {
      field: 'document_number',
      headerName: 'Self Billed Refund No',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography
            variant="body1"
            sx={{
              textDecoration: 'underline',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={() => navigate(`/self-billed-documents/refunds/${row.document_id}`)}
          >
            {row.document_number}
          </Typography>
        );
      },
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice Number',
      flex: 1,
    },
    {
      field: 'billed_to',
      headerName: 'Customer Info',
      flex: 1,
      valueGetter: (params) => {
        return params.name;
      },
    },
    {
      field: 'document_date',
      headerName: 'Refund Date',
      flex: 1,
      renderCell: ({ row }) => {
        return <span>{getDateInMysTzFormatted(row.document_date, row.document_time)}</span>;
      },
    },
    {
      field: 'grand_total',
      headerName: 'Amount',
      flex: 1,
      renderCell: ({ row }) =>
        row.document_currency_code === 'MYR'
          ? `RM ${currencyFormatter().format(row.grand_total)}`
          : currencyFormatter(row.document_currency_code).format(row.grand_total),
    },
    {
      field: 'row',
      headerName: 'Actions',
      flex: 0.5,
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', width: '100%', gap: 0.6 }}>
          {get(row, 'irbm_response.status') === 'Valid' && (
            <>
              <Tooltip title="Download Document">
                <IconButton size="small" onClick={() => downloadPdf(row.document_type, row, row.document_id)}>
                  <PictureAsPdfIcon fontSize="small" color="error" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print Document">
                <IconButton size="small" onClick={() => handlePrintPdf(row.document_type, row, row.document_id)}>
                  <PrintIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <DocumentActions
            row={row}
            documentStatus={{ key: 'irbm_response.status' }}
            documentType={row.document_type}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <DataGrid
        rows={refunds}
        columns={columns}
        getRowId={(row) => row._id}
        paginationMode="server"
        rowCount={totalDocuments}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10, 25]}
        disableRowSelectionOnClick
        onPaginationModelChange={(model) => {
          handlePaginationChange(model.page + 1, model.pageSize);
        }}
        slots={{
          pagination: CustomPagination,
        }}
      />
    </Box>
  );
};

export default SBRefundTable;
